import { API_URL, SITE } from '../config';

/** Error thrown by every API call. `code` follows the contract (`{error, detail}`), plus two client-side codes. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly detail: unknown;
  /** Seconds from a Retry-After header, when the server sent one. */
  readonly retryAfter: number | null;

  constructor(status: number, code: string, detail: unknown = null, retryAfter: number | null = null) {
    super(`${code}${typeof detail === 'string' && detail ? `: ${detail}` : ''}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.detail = detail;
    this.retryAfter = retryAfter;
  }
}

/** Client-side error codes (never sent by the server). */
export const NETWORK_ERROR = 'network_error';
export const BAD_RESPONSE = 'bad_response';

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

export function isUnauthorized(e: unknown): boolean {
  return isApiError(e) && e.status === 401;
}

type Fetch = typeof fetch;

export interface ClientOptions {
  baseUrl?: string;
  site?: string;
  fetch?: Fetch;
  /** Called on any 401 that is not part of the login flow, so the app can drop to the login screen. */
  onUnauthorized?: () => void;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Do not trigger onUnauthorized (login endpoints, /auth/me probe). */
  skipAuthRedirect?: boolean;
}

function parseRetryAfter(h: string | null): number | null {
  if (!h) return null;
  const n = Number(h);
  if (Number.isFinite(n) && n >= 0) return Math.ceil(n);
  const d = Date.parse(h);
  return Number.isNaN(d) ? null : Math.max(0, Math.ceil((d - Date.now()) / 1000));
}

/** Turn a non-2xx response into an ApiError. Handles the contract shape, FastAPI 422 `{detail:[...]}`, and non-JSON bodies. */
export async function toApiError(res: Response): Promise<ApiError> {
  const retryAfter = parseRetryAfter(res.headers.get('retry-after'));
  let data: unknown = null;
  try {
    const text = await res.text();
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  if (data && typeof data === 'object') {
    const o = data as Record<string, unknown>;
    if (typeof o.error === 'string') return new ApiError(res.status, o.error, o.detail ?? null, retryAfter);
    if ('detail' in o) return new ApiError(res.status, res.status === 422 ? 'validation_error' : `http_${res.status}`, o.detail, retryAfter);
  }
  const fallback: Record<number, string> = { 401: 'unauthorized', 403: 'forbidden', 404: 'not_found', 429: 'rate_limited' };
  return new ApiError(res.status, fallback[res.status] ?? `http_${res.status}`, null, retryAfter);
}

export function createClient(opts: ClientOptions = {}) {
  const base = (opts.baseUrl ?? API_URL).replace(/\/+$/, '');
  const site = opts.site ?? SITE;
  let onUnauthorized = opts.onUnauthorized;

  async function request<T>(path: string, ro: RequestOptions = {}): Promise<T> {
    const f: Fetch = opts.fetch ?? globalThis.fetch.bind(globalThis);
    const headers: Record<string, string> = { Accept: 'application/json', 'X-Site': site };
    let body: BodyInit | undefined;
    if (ro.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(ro.body);
    }
    let res: Response;
    try {
      res = await f(`${base}${path}`, {
        method: ro.method ?? (ro.body !== undefined ? 'POST' : 'GET'),
        headers,
        body,
        credentials: 'include',
        signal: ro.signal,
      });
    } catch (e) {
      if ((e as { name?: string })?.name === 'AbortError') throw e;
      throw new ApiError(0, NETWORK_ERROR, e instanceof Error ? e.message : null);
    }
    if (!res.ok) {
      const err = await toApiError(res);
      if (err.status === 401 && !ro.skipAuthRedirect) onUnauthorized?.();
      throw err;
    }
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    if (!text) return undefined as T;
    try {
      return JSON.parse(text) as T;
    } catch {
      throw new ApiError(res.status, BAD_RESPONSE, null);
    }
  }

  return {
    request,
    get: <T>(path: string, ro?: Omit<RequestOptions, 'method' | 'body'>) => request<T>(path, { ...ro, method: 'GET' }),
    post: <T>(path: string, body?: unknown, ro?: Omit<RequestOptions, 'method' | 'body'>) =>
      request<T>(path, { ...ro, method: 'POST', body: body ?? {} }),
    setOnUnauthorized(fn: (() => void) | undefined) {
      onUnauthorized = fn;
    },
  };
}

export type ApiClient = ReturnType<typeof createClient>;

/** App-wide singleton. Tests create their own clients with a mocked fetch. */
export const api = createClient();
